<template>
  <!-- TKP-203: envanter özelliklerini aynı süreçteki başka bir veriden / ilgili kişiden kopyala (kip Ekle) -->
  <b-modal :id="modalId" size="lg" :title="$t(kind === 'row' ? 'inventory.copy.title-row' : 'inventory.copy.title-node')" no-close-on-backdrop data-test="copy-features" @hidden="$emit('hidden')">
    <content-loading v-if="loading" />
    <template v-else>
      <p class="mb-2" data-test="copy-targets">
        <span class="text-muted">{{ $t("inventory.copy.targets") }}:</span> <b>{{ targetNames }}</b>
      </p>
      <p v-if="!sourceOptions.length" class="text-muted" data-test="copy-no-source">{{ $t(kind === "row" ? "inventory.copy.no-source-row" : "inventory.copy.no-source-node") }}</p>
      <template v-else>
        <!-- TKP-227: veri sayfasında da ilgili kişi sayfasındaki akış — tek kaynak ("Veri › İlgili kişi"; kişisiz veride veri adı),
             listede yalnız özelliği dolu kombinasyonlar; seçimden sonra dolu bölümler seçilir; ikisi seçilmeden Kopyala çalışmaz -->
        <h6>{{ $t(kind === "row" ? "inventory.copy.source-row" : "inventory.copy.source-pairs") }}</h6>
        <b-form-select v-model="sourceId" :options="sourceOptions" class="mb-3" data-test="copy-source">
          <template #first><b-form-select-option :value="null" disabled>{{ $t("common.select") }}</b-form-select-option></template>
        </b-form-select>
        <template v-if="hasSource">
          <p v-if="!sections.length" class="text-muted" data-test="copy-empty-source">{{ $t("inventory.copy.empty-source") }}</p>
          <template v-else>
            <h6>{{ $t("inventory.copy.sections") }}</h6>
            <b-form-checkbox-group v-model="selected" stacked data-test="copy-sections">
              <b-form-checkbox v-for="s in sections" :key="s.key" :value="s.key" :data-test="'copy-section-' + s.key">
                {{ $t(s.label) }} <span class="badge badge-light ml-1">{{ s.count }}</span>
              </b-form-checkbox>
            </b-form-checkbox-group>
            <p class="text-muted small mt-2 mb-1"><i class="bx bx-info-circle"></i> {{ $t("inventory.copy.add-hint") }}</p>
            <p v-if="unmatchedNames" class="text-warning small mb-1" data-test="copy-unmatched"><i class="bx bx-error"></i> {{ $t("inventory.copy.unmatched", { names: unmatchedNames }) }}</p>
            <p class="small mb-0" data-test="copy-preview">{{ $t("inventory.copy.preview", { count: plan.steps.length }) }}</p>
          </template>
        </template>
      </template>
    </template>
    <template #modal-footer="{ cancel }">
      <b-button variant="light" size="sm" @click="cancel()">{{ $t("common.cancel") }}</b-button>
      <b-button variant="success" size="sm" :disabled="applying || !canApply" data-test="copy-apply" @click="apply">
        <i class="bx" :class="applying ? 'bx-loader bx-spin' : 'bx-copy'"></i> {{ $t("inventory.copy.apply") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import rest from "@/rest-client";
import { overlayDatasubjectInventory, overlayNodeInventory, withShadowNodes, withShadowRows, draftNodeMeasuresOf } from "@modules/approval/draft-overlay";
import { flattenNodes, hierarchyCriteria } from "./hierarchy-inventory";
import { copySections, datasubjectKeyOf, nodeCopyPlan, resolveRefs, rowCopyPlan, sourceSnapshots } from "./copy-features";
import { savedIdOf } from "@/modules/process-data/unified-picker";

let seq = 0;
const sid = (x) => String(x && x._id ? x._id : x || "");

/**
 * TKP-203 — hedef(ler): veri kopyasında `targetNodeIds`, ilgili kişi kopyasında `targetRowIds`. Kaynak aynı süreçten seçilir; bölümler
 * kaynağın dolu bölümleri (hepsi işaretli). Yazım panelin adımlarıyla, hedef başına sırayla; onay açıkken her adım taslağa düşer.
 * Açma: `ref.open()`; bitince `copied` yayılır.
 */
export default {
  name: "copy-features-dialog",
  props: {
    department: { type: Object, required: true },
    process: { type: Object, required: true },
    targetNodeIds: { type: Array, default: () => [] },
    targetRowIds: { type: Array, default: () => [] },
  },
  data() {
    return { uid: ++seq, loading: false, applying: false, nodes: [], nodeInv: {}, rowInv: {}, sourceId: null, selected: [] };
  },
  computed: {
    modalId() {
      return `copy-features-${this.uid}`;
    },
    kind() {
      return this.targetRowIds.length ? "row" : "node";
    },
    basePath() {
      return this.currentProduct == "GDPR" ? "/gdpr/inventory" : "/kvkk/inventory";
    },
    tr() {
      return (v) => this.$options.filters.translate(v) || "";
    },
    /** Satırlar verisiyle: { row, node }. */
    rows() {
      return this.nodes.flatMap((node) => (node.datasubjects || []).map((row) => ({ row, node })));
    },
    targetNodes() {
      const ids = this.targetNodeIds.map(sid);
      return this.nodes.filter((n) => ids.includes(sid(n)));
    },
    targetRows() {
      const ids = this.targetRowIds.map(sid);
      return this.rows.filter((r) => ids.includes(sid(r.row)));
    },
    targetNames() {
      return this.kind === "row" ? this.targetRows.map((r) => `${this.tr(r.node.data && r.node.data.name)} › ${this.tr(r.row.name)}`).join(", ") : this.targetNodes.map((n) => this.tr(n.data && n.data.name)).join(", ");
    },
    /** Kaynak seçenekleri (her iki kip): yalnız kopyalanacak özelliği DOLU olanlar (TKP-227). */
    sourceOptions() {
      const filled = (snaps) => copySections(snaps).length > 0;
      if (this.kind === "row") {
        const targets = new Set(this.targetRowIds.map(sid));
        return this.rows
          .filter((r) => !targets.has(sid(r.row)) && !r.row.__draft)
          .filter((r) => filled(sourceSnapshots({ kind: "row", sourceRow: r.row, nodeInv: this.nodeInv, rowInv: this.rowInv })))
          .map((r) => ({ value: sid(r.row), text: `${this.tr(r.node.data && r.node.data.name)} › ${this.tr(r.row.name)}` }));
      }
      // "Veri › İlgili kişi" çiftleri. TKP-235 m.192: ilgili kişisi olmayan veri kaynak olarak listelenmez (özellikleri düzenlenemez)
      const targets = new Set(this.targetNodeIds.map(sid));
      return this.nodes
        .filter((n) => !targets.has(sid(n)) && !n.__draft)
        .flatMap((n) => {
          const rows = (n.datasubjects || []).filter((r) => !r.__draft);
          const data = this.tr(n.data && n.data.name);
          if (!rows.length) return [];
          return rows.map((r) => ({ value: `r:${sid(r)}`, nodeId: sid(n), rowId: sid(r), text: `${data} › ${this.tr(r.name)}` }));
        })
        .filter((o) => filled(sourceSnapshots({ kind: "node", sourceNode: this.nodes.find((n) => sid(n) === o.nodeId), nodeInv: this.nodeInv, rowInv: this.rowInv, rowIds: o.rowId ? [o.rowId] : [] })));
    },
    /** Veri kopyasında seçilen kaynak — { node, rowIds }. */
    sourceGroups() {
      if (this.kind !== "node" || !this.sourceId) return [];
      const o = this.sourceOptions.find((x) => x.value === this.sourceId);
      const node = o && this.nodes.find((n) => sid(n) === o.nodeId);
      return node ? [{ node, rowIds: o.rowId ? [o.rowId] : [] }] : [];
    },
    hasSource() {
      return !!this.sourceId;
    },
    /** TKP-227: kaynak VE en az bir bölüm seçili, yazılacak kayıt var. */
    canApply() {
      return this.hasSource && this.selected.length > 0 && this.plan.steps.length > 0;
    },
    sourceRow() {
      return this.kind === "row" ? (this.rows.find((r) => sid(r.row) === this.sourceId) || {}).row || null : null;
    },
    sections() {
      if (!this.hasSource) return [];
      if (this.kind === "row") return copySections(sourceSnapshots({ kind: "row", sourceRow: this.sourceRow, nodeInv: this.nodeInv, rowInv: this.rowInv }));
      return copySections(this.sourceGroups.flatMap((g) => sourceSnapshots({ kind: "node", sourceNode: g.node, nodeInv: this.nodeInv, rowInv: this.rowInv, rowIds: g.rowIds })));
    },
    /** Bütün hedeflerin birleşik planı (önizleme ve yazım aynı plan). */
    plan() {
      const out = { steps: [], skipped: [], unmatched: [] };
      if (!this.hasSource) return out;
      if (this.kind === "row") {
        for (const t of this.targetRows) {
          const p = rowCopyPlan({ sourceRow: this.sourceRow, targetRow: t.row, targetNode: t.node, nodeInv: this.nodeInv, rowInv: this.rowInv, sections: this.selected });
          out.steps.push(...p.steps);
          out.skipped.push(...p.skipped);
        }
        return out;
      }
      const drafts = (this.$store.getters || {})["approval/drafts"];
      const me = this.currentAccount && this.currentAccount._id;
      for (const node of this.targetNodes) {
        const pending = draftNodeMeasuresOf(drafts, sid(node), me);
        // TKP-222 p.7: kaynak veri başına plan; bir grupta hedefe eklenecek kişi sonraki gruplarda "var" sayılır (aynı kişi iki kez açılmaz)
        let target = node;
        for (const g of this.sourceGroups) {
          const p = nodeCopyPlan({ sourceNode: g.node, targetNode: target, nodeInv: this.nodeInv, rowInv: this.rowInv, sections: this.selected, existingMeasures: pending || undefined, rowIds: g.rowIds });
          out.steps.push(...p.steps);
          out.skipped.push(...p.skipped);
          for (const row of p.unmatched) if (!out.unmatched.some((r) => sid(r) === sid(row))) out.unmatched.push(row);
          const added = p.steps.filter((st) => st.action === "createRow").map((st) => ({ _id: st.ref, datasubject: st.payload.datasubject, name: {}, feature: {} }));
          if (added.length) target = { ...target, datasubjects: [...(target.datasubjects || []), ...added] };
        }
      }
      return out;
    },
    unmatchedNames() {
      return this.plan.unmatched.map((r) => this.tr(r.name)).filter(Boolean).join(", ");
    },
  },
  watch: {
    sourceId() {
      this.selected = this.sections.map((s) => s.key); // kullanıcı kararı: hepsi işaretli gelir
    },
  },
  methods: {
    async open() {
      this.sourceId = null;
      this.selected = [];
      this.$bvModal.show(this.modalId);
      await this.load();
      this.sourceId = this.defaultSource();
    },
    /** İlgili kişi kopyasında aynı ilgili kişiyi taşıyan başka satır hazır seçili (yeni veriye eklenen kişinin süreçteki eşi). */
    defaultSource() {
      if (this.kind !== "row" || !this.targetRows.length) return null;
      const key = datasubjectKeyOf(this.targetRows[0].row);
      const twin = this.sourceOptions.find((o) => {
        const r = this.rows.find((x) => sid(x.row) === o.value);
        return r && datasubjectKeyOf(r.row) === key;
      });
      return twin ? twin.value : null;
    },
    async load() {
      this.loading = true;
      try {
        const shadowScope = !!this.process.__draft;
        const { data } = shadowScope
          ? { data: { docs: [] } }
          : await rest.post(`${this.basePath}/paginate`, { pagination: { options: { page: 1, limit: 1000 }, query: {} }, criteria: hierarchyCriteria({ department: this.department, process: this.process }) });
        const drafts = (this.$store.getters || {})["approval/drafts"];
        const me = this.currentAccount && this.currentAccount._id;
        const tree = withShadowRows(withShadowNodes((data && data.docs) || [], drafts, me, this.department), drafts, me);
        this.nodes = flattenNodes(tree).filter((e) => sid(e.process) === sid(this.process)).map((e) => e.node);
        const processDatas = this.nodes.filter((n) => !n.__draft).map((n) => ({ _id: n._id }));
        const datasubjects = this.nodes.flatMap((n) => (n.datasubjects || []).filter((r) => !r.__draft).map((r) => ({ _id: r._id })));
        const empty = Promise.resolve({ data: {} });
        const [ni, si] = await Promise.all([
          processDatas.length ? rest.post(`${this.basePath}/fetch-process-data`, { processDatas }) : empty,
          datasubjects.length ? rest.post(`${this.basePath}/fetch-datasubject`, { datasubjects }) : empty,
        ]);
        this.nodeInv = overlayNodeInventory(ni.data || {}, processDatas, drafts, me);
        this.rowInv = overlayDatasubjectInventory(si.data || {}, this.nodes.flatMap((n) => n.datasubjects || []), drafts, me);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.loading = false;
      }
    },
    /** Adımlar sırayla; biri hata verse de diğerleri sürer (her adım bağımsız kayıt), ilk hata gösterilir. */
    async apply() {
      const steps = this.plan.steps;
      if (!this.canApply || !steps.length) return;
      this.applying = true;
      let written = 0;
      let drafted = 0;
      let failed = null;
      try {
        const refs = {}; // TKP-214: hedefe eklenen ilgili kişi satırlarının kimlikleri (yer tutucu → kimlik)
        for (const step of steps) {
          try {
            if (step.store === "organizationProcessData/update") this.$store.commit("organizationProcessData/editedEntity", undefined); // TKP-200 m.17
            const payload = resolveRefs(step.payload, refs);
            if (JSON.stringify(payload).includes("@yeni-satir:")) { failed = failed || new Error("datasubject row not created"); continue; } // satırı açılamadı — adımı atla
            const response = await this.$store.dispatch(step.store, payload);
            if (step.ref) refs[step.ref] = savedIdOf(response);
            // taslak yanıtını axios kesicisi kayıt biçimine çevirir (`__draft`); ham biçim (`draft`) de tanınır
            const data = response && response.data;
            const isDraft = (d) => !!(d && (d.__draft || d.draft));
            if (isDraft(data) || (Array.isArray(data) && data.some(isDraft))) drafted++;
            else written++;
          } catch (error) {
            failed = failed || error;
          }
        }
        if (failed) this.$handleHttpErrorByStatusCode(failed);
        if (drafted) {
          this.$toast.success(this.$t("inventory.copy.done-draft", { count: drafted }));
          await this.$store.dispatch("approval/fetch").catch(() => undefined);
        }
        if (written) this.$toast.success(this.$t("inventory.copy.done", { count: written }));
        this.$bvModal.hide(this.modalId);
        this.$emit("copied");
      } finally {
        this.applying = false;
      }
    },
  },
};
</script>
